import { useParameterStore } from '@/frontend/store/ParameterStore';
import { ChangeEvent, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { OutputParameterSelect } from '@/frontend/components/parameters/OutputParameterSelect';
import { InputParameterSelect } from '@/frontend/components/parameters/InputParameterSelect';
import { I18n } from '@/i18n/I18n';
import { SliderControlProps, SliderType } from './types';
import { getSliderColor } from './graphUtils';

const MAX_TITLE_LENGTH = 32;

export function SliderControl({
  slider,
  index,
  runView,
  onDeleteSlider,
  onAutoMax,
  canAutoMax,
  operation,
  disabled = false
}: SliderControlProps) {

  const color = getSliderColor(index);
  const { updateParameter } = useParameterStore();
  const [title, setTitle] = useState(slider.name);

  const updateTitle = async (event: ChangeEvent<HTMLInputElement>) => {
    const newTitle = event.target.value;
    await updateParameter({ parameter: { ...slider.parameter, title: newTitle } });
  };

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateTitleTimeout = (event: ChangeEvent<HTMLInputElement>) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      void updateTitle(event);
    }, 500);
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    updateTitleTimeout(event);
  };

  return (
    <div key={slider.id} className="p-1 pb-3 mb-3 border-bottom">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center" style={{ flex: '1 1 auto', minWidth: 0, marginRight: '8px' }}>
          <div
            className="me-2 flex-shrink-0"
            style={{
              width: '12px',
              height: '12px',
              backgroundColor: color,
              borderRadius: '2px',
              border: '1px solid #333'
            }}
          />
          <Form.Label
            htmlFor={`slider-${slider.id}`}
            className="small mb-0 text-truncate max-w-100"
            title={`${title}: ${slider.value}`}
          >
            {title}: {slider.value}
          </Form.Label>
        </div>
        {!runView && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => onDeleteSlider(slider.id)}
            className="flex-shrink-0"
            style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            disabled={disabled}
          >
            ×
          </Button>
        )}
      </div>

      {!runView && (
        <div className="mb-2">
          <Form.Control
            type="text"
            size="sm"
            value={title}
            onChange={handleTitleChange}
            placeholder={I18n.get('enterSliderLabel')}
            maxLength={MAX_TITLE_LENGTH}
            disabled={disabled}
          />
        </div>
      )}

      {!runView && slider.type === SliderType.Y && onAutoMax && (
        <div className="mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => onAutoMax(slider.id)}
            disabled={disabled || !canAutoMax}
            title={canAutoMax ? I18n.get('graphAutoMaxHelp') : I18n.get('graphAutoMaxRequiresXSliders')}
          >
            {I18n.get('graphAutoMax')}
          </Button>
        </div>
      )}

      {!runView && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="form-label col-form-label-sm">{I18n.get('assignValueFrom')}</Form.Label>
            <InputParameterSelect operation={operation} parameter={slider.parameter} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="form-label col-form-label-sm">{I18n.get('assignValueTo')}</Form.Label>
            <OutputParameterSelect operation={operation} parameter={slider.parameter} />
          </Form.Group>
        </>
      )}
    </div>
  );
}

